<template>
    <v-card>
        <v-card-title class="text-subtitle-1">Facility load</v-card-title>
        <v-card-text>
            <div class="pavo-heat-row">
                <div v-for="month in monthGrids" :key="month.label" class="pavo-heat-month">
                    <h3>{{ month.label }}</h3>
                    <div class="pavo-heat-grid">
                        <div v-for="dow in daysOfWeek" :key="dow.key" class="pavo-heat-dow">
                            {{ dow.label }}
                        </div>
                        <div
                            v-for="cell in month.cells"
                            :key="cell.key"
                            class="pavo-heat-day"
                            :class="cell.level > 0 ? `pavo-heat-level-${cell.level}` : undefined"
                            :style="cell.day === null ? 'visibility: hidden' : undefined"
                        >
                            {{ cell.day ?? "" }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="pavo-heat-legend">
                <span>Functions/day:</span>
                <span><i class="pavo-heat-level-1" />1-3</span>
                <span><i class="pavo-heat-level-2" />4-7</span>
                <span><i class="pavo-heat-level-3" />8+</span>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FunctionCountDay } from "@a/dashboard";
import { parseIsoDate, toIsoDate } from "./format";

type Props = {
    from: string;
    counts: FunctionCountDay[];
    months?: number;
};

const props = defineProps<Props>();

const daysOfWeek = [
    { key: "sun", label: "S" },
    { key: "mon", label: "M" },
    { key: "tue", label: "T" },
    { key: "wed", label: "W" },
    { key: "thu", label: "T" },
    { key: "fri", label: "F" },
    { key: "sat", label: "S" },
];

type Cell = {
    key: string;
    day: number | null;
    level: number;
};

type MonthGrid = {
    label: string;
    cells: Cell[];
};

function heatLevel(count: number): number {
    if (count >= 8) {
        return 3;
    }
    if (count >= 4) {
        return 2;
    }
    return count >= 1 ? 1 : 0;
}

const monthGrids = computed<MonthGrid[]>(() => {
    const countByDate = new Map(props.counts.map((day) => [day.date, day.count]));
    const start = parseIsoDate(props.from);
    const grids: MonthGrid[] = [];
    for (let offset = 0; offset < (props.months ?? 3); offset++) {
        const first = new Date(start.getFullYear(), start.getMonth() + offset, 1);
        const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
        const cells: Cell[] = [];
        for (let pad = 0; pad < first.getDay(); pad++) {
            cells.push({ key: `pad-${pad}`, day: null, level: 0 });
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = toIsoDate(new Date(first.getFullYear(), first.getMonth(), day));
            cells.push({ key: date, day, level: heatLevel(countByDate.get(date) ?? 0) });
        }
        grids.push({
            label: first.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
            cells,
        });
    }
    return grids;
});
</script>

<style lang="scss">
.pavo-heat-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

.pavo-heat-month {
    font-size: 12px;

    h3 {
        font-size: 13px;
        font-weight: 500;
        margin: 0 0 8px;
        text-align: center;
    }
}

.pavo-heat-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    text-align: center;
}

.pavo-heat-dow {
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 10px;
    padding: 2px 0;
}

.pavo-heat-day {
    padding: 3px 0;
    border-radius: 4px;
}

.pavo-heat-level-1 {
    background: #9fd0c9;
}

.pavo-heat-level-2 {
    background: #4da99d;
    color: #fff;
}

.pavo-heat-level-3 {
    background: #00796b;
    color: #fff;
}

.v-theme--dark {
    .pavo-heat-level-1 {
        background: #2c4a45;
        color: inherit;
    }

    .pavo-heat-level-2 {
        background: #3d7d74;
    }

    .pavo-heat-level-3 {
        background: #53b8ab;
        color: #131917;
    }
}

.pavo-heat-legend {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 11px;

    i {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 3px;
        margin-right: 4px;
    }
}
</style>
